import { Injectable } from '@angular/core'
import {
  IRangeDateInfo,
  IStringTimePeriod,
  ITimePeriodRequest,
  PeriodType,
  PeriodTypeButtonsInfo,
  ReportPeriods
} from '@lla-platform/core/core-data-access'
import { IRangeDateValue } from '@lla-platform/core/core-ui'
import moment, { Moment } from 'moment'

@Injectable({
  providedIn: 'root'
})
export class DateService {
  momentToDateString(date: string | Date | Moment | undefined) {
    if (!date) {
      return
    }
    return moment(date).utc().format().split('T')[0]
  }

  momentCalculatedFormattedPeriod(rangeDate: IRangeDateValue | undefined, withoutYear = false) {
    let formattedPeriodTime = ''
    if (!rangeDate || !rangeDate?.from || !rangeDate?.to) {
      return
    }

    const { from, to } = {
      from: moment(rangeDate.from).utc(),
      to: moment(rangeDate.to).utc()
    }
    formattedPeriodTime +=
      from.isSame(to, 'year') || withoutYear ? from.format('MMM D') : from.format('MMM D, YYYY')
    formattedPeriodTime += ` - ${withoutYear ? to.format('MMM D') : to.format('MMM D, YYYY')}`

    return formattedPeriodTime
  }

  getFullDateWithoutTimeZone(info: { date: Date; hour?: number; min?: number }) {
    const date = new Date(info.date)
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}T${
      info.hour ? (info.hour > 9 ? info.hour : `0${info.hour}`) : '00'
    }:${info.min ? (info.min > 9 ? info.min : `0${info.min}`) : '00'}:00.000Z`
  }

  periodTypeToDate(periodType: PeriodType): Required<IRangeDateValue> {
    let dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === periodType)?.extraInfo
    if (!dateInfo) {
      dateInfo = PeriodTypeButtonsInfo().find((el) => el.value === PeriodType.WeekToDate)?.extraInfo
    }

    return { from: dateInfo.from.format(), to: dateInfo.to.format() }
  }

  generateTimePeriod(value: Partial<ITimePeriodRequest>, shouldConvert = true): IStringTimePeriod {
    if (shouldConvert) {
      return {
        from: value?.from ? this.momentToDateString(value.from) : undefined,
        to: value?.to ? this.momentToDateString(value.to) : undefined
      }
    } else {
      return {
        from: value?.from as string,
        to: value?.to as string
      }
    }
  }

  periodTypeToDateString(periodType: PeriodType) {
    const dateInfo = this.periodTypeToDate(periodType)
    return this.generateTimePeriod(dateInfo)
  }

  tooltipFormattedDate(
    dateString: string,
    selectedPeriod: ReportPeriods,
    selectedDateInfo: IRangeDateInfo,
    dataIndex: number,
    dateFormat: string,
    fullDataLength: number
  ) {
    if (selectedPeriod === 'dayOfWeek') {
      return dateString
    }
    let tooltipFormattedDate = moment(dateString).format(dateFormat)
    if (selectedPeriod === 'weekly') {
      const maxDataIndex = fullDataLength - 1
      if (selectedDateInfo.rangeDate && (dataIndex === 0 || dataIndex === maxDataIndex)) {
        const { from, to } = selectedDateInfo.rangeDate
        const startOfWeek = moment
          .max(moment(from).utc(), moment(dateString).startOf('isoWeek').utcOffset(0, true))
          .format(dateFormat)

        const endOfWeek = moment
          .min(moment(to).utc(), moment(dateString).endOf('isoWeek').utcOffset(0, true))
          .format(dateFormat)
        tooltipFormattedDate = `${startOfWeek} - ${endOfWeek}`
      } else {
        tooltipFormattedDate = `${moment(dateString).startOf('isoWeek').format(dateFormat)} - ${moment(dateString).endOf('isoWeek').format(dateFormat)}`
      }
    } else if (selectedPeriod === 'monthly') {
      tooltipFormattedDate = moment(dateString).format('MMMM, YYYY')
    }

    return tooltipFormattedDate
  }

  getAllWeeks(includeFuture = false) {
    const startDate = moment('2024-01-01')
    const currentDate = moment()
    const endDate = includeFuture ? moment().endOf('year').startOf('isoWeek') : currentDate
    const allWeeks = []

    // Iterate through all weeks from start of 2024 to current/end-of-year week
    let weekStart = startDate.clone().startOf('isoWeek')

    while (weekStart.isSameOrBefore(endDate, 'isoWeek')) {
      const weekEnd = weekStart.clone().endOf('isoWeek')
      const weekNumber = weekStart.isoWeek()

      const year = weekStart.isoWeekYear()

      const label = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`

      // Value as year + week number (e.g., 202546 for 2025 week 46)
      const value = year * 100 + weekNumber

      allWeeks.push({
        label,
        value,
        extraInfo: year.toString()
      })

      // Move to next week
      weekStart = weekStart.add(1, 'week')
    }

    return allWeeks
  }

  // Get last week by subtracting 1 week from current date
  getLastWeekNumber() {
    const lastWeek = moment().subtract(1, 'week').startOf('isoWeek')
    const weekNumber = lastWeek.isoWeek()
    const year = lastWeek.isoWeekYear()
    return year * 100 + weekNumber
  }

  // Resolve a year*100+isoWeek number to the prior ISO week (handles year boundaries)
  getPriorWeekNumber(weekNumber: number) {
    const year = Math.floor(weekNumber / 100)
    const week = weekNumber % 100
    const prior = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek').subtract(1, 'week')
    return prior.isoWeekYear() * 100 + prior.isoWeek()
  }

  // Date-range label for a year*100+isoWeek number, e.g. "Jan 6 - Jan 12, 2025"
  getWeekNumberLabel(weekNumber: number) {
    const year = Math.floor(weekNumber / 100)
    const week = weekNumber % 100
    const weekStart = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
    const weekEnd = weekStart.clone().endOf('isoWeek')
    return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}, ${year}`
  }

  getYearList() {
    const currentYear = new Date().getFullYear()
    return [currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((el) => ({
      label: `${el}`,
      value: el
    }))
  }
}
